import React, { useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import { XMarkIcon } from "@heroicons/react/24/outline"
import { GetTags } from "./api"
import { showNotification } from "aldoo-ra/Notification"

const AddSubscriberDialog = ({
  isOpen,
  onClose,
  onAdd,
  editRule = null, // For editing existing rules
}) => {
  const [subscriber, setSubscriber] = useState(editRule?.subscriber || "")
  const [selectedTags, setSelectedTags] = useState(editRule?.tags || [])
  const [availableTags, setAvailableTags] = useState([])
  const [newTag, setNewTag] = useState("")
  const [loading, setLoading] = useState(false)

  // Load available tags when dialog opens
  React.useEffect(() => {
    async function loadTags() {
      try {
        const response = await GetTags()
        if (response.result) {
          const tagOptions = response.data.map((tag) => ({
            value: tag,
            name: tag,
          }))
          setAvailableTags(tagOptions)
        }
      } catch (error) {
        showNotification({
          message: "Failed to load tags",
          className: "bg-admin_error text-white",
        })
      }
    }

    if (isOpen) {
      loadTags()
      // Reset or load edit data
      if (editRule) {
        setSubscriber(editRule.subscriber)
        setSelectedTags(editRule.tags)
      } else {
        setSubscriber("")
        setSelectedTags([])
      }
      setNewTag("")
    }
  }, [isOpen, editRule])

  const handleAddCustomTag = () => {
    if (!newTag.trim()) return

    const trimmedTag = newTag.trim()

    // Check if tag already exists in selected tags
    const tagExists = selectedTags.some(
      (tag) => (tag.value || tag.name || tag) === trimmedTag
    )

    if (!tagExists) {
      const newTagObject = { value: trimmedTag, name: trimmedTag }
      setSelectedTags([...selectedTags, newTagObject])
      setNewTag("")
    } else {
      showNotification({
        message: "Tag already added",
        className: "bg-admin_error text-white",
      })
    }
  }

  const handleRemoveTag = (tagToRemove) => {
    setSelectedTags(
      selectedTags.filter((tag) => {
        const tagValue = tag.value || tag.name || tag
        const removeValue = tagToRemove.value || tagToRemove.name || tagToRemove
        return tagValue !== removeValue
      })
    )
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleAddCustomTag()
    }
  }

  // Convert selected tags to format expected by dropdown
  const selectedTagsForDropdown = selectedTags.map((tag) => ({
    value: tag.value || tag.name || tag,
    name: tag.name || tag.value || tag,
  }))

  const handleAdd = () => {
    if (!subscriber.trim()) {
      showNotification({
        message: "Please enter a subscriber email",
        className: "bg-admin_error text-white",
      })
      return
    }

    if (selectedTags.length === 0) {
      showNotification({
        message: "Please select at least one tag",
        className: "bg-admin_error text-white",
      })
      return
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(subscriber.trim())) {
      showNotification({
        message: "Please enter a valid email address",
        className: "bg-admin_error text-white",
      })
      return
    }

    setLoading(true)
    onAdd({
      subscriber: subscriber.trim(),
      tags: selectedTags,
      _id: editRule?._id, // Include ID if editing
    })
    setLoading(false)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="admin_centered"
      className="w-full md:w-[600px]"
    >
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <Typography variant="admin_h3">
            {editRule ? "Edit Forward Rule" : "Add Forward Rule"}
          </Typography>
          <Typography variant="admin_body" className="text-admin_text_secondary mt-2">
            Tickets matching any of the selected tags will be forwarded to the subscriber's email
          </Typography>
        </div>

        {/* Subscriber Email Input */}
        <div className="mb-6">
          <Typography variant="admin_body_accent" className="mb-2">
            Subscriber Email
          </Typography>
          <Input
            variant="admin"
            value={subscriber}
            onChange={(e) => setSubscriber(e.target.value)}
            placeholder="email@company.com"
            className="w-full"
          />
          <Typography variant="admin_txt1" className="text-admin_text_secondary mt-1">
            Tickets will be forwarded to this email address
          </Typography>
        </div>

        {/* Add Tag Input and Dropdown */}
        <div className="mb-6 flex items-center gap-4">
          <div className="flex-grow relative">
            <Input
              variant="admin"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Add Custom Tag"
              className="pr-[65px]"
            />
            {newTag.trim() && (
              <Button
                variant="admin_primary"
                onClick={handleAddCustomTag}
                className="w-[60px] absolute right-[5px] top-1/2 transform -translate-y-1/2 text-xs"
              >
                Add
              </Button>
            )}
          </div>

          <div className="w-48">
            <DropDownSelect
              variant="admin"
              options={availableTags}
              value={selectedTagsForDropdown}
              onChange={setSelectedTags}
              searchEnabled
              multiSelection={true}
              placeholder="Select Tag"
              valueField="value"
              nameField="name"
              className="w-full"
            />
          </div>
        </div>

        {/* Tags Display Area */}
        <div className="min-h-[120px] border border-admin_bordered rounded-md p-4 mb-6">
          <Typography variant="admin_body_accent" className="mb-2">
            Selected Tags ({selectedTags.length})
          </Typography>
          <div className="flex flex-wrap gap-2">
            {selectedTags.map((tag, index) => (
              <div
                key={index}
                className="inline-flex items-center bg-admin_info_bg text-admin_text rounded-full px-3 py-1"
              >
                <Typography variant="admin_txt1">
                  {tag.name || tag.value || tag}
                </Typography>
                <button
                  onClick={() => handleRemoveTag(tag)}
                  className="ml-2 hover:text-admin_text_secondary"
                >
                  <XMarkIcon className="w-4 h-4 stroke-2" />
                </button>
              </div>
            ))}
            {selectedTags.length === 0 && (
              <Typography variant="admin_body_dim" className="my-0">
                No tags added - add custom tags or select from dropdown
              </Typography>
            )}
          </div>
        </div>

        {/* Action Button */}
        <div className="flex justify-end">
          <Button
            variant="admin_primary"
            onClick={handleAdd}
            disabled={loading}
          >
            {loading ? "Saving..." : editRule ? "Update Rule" : "Add Rule"}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default AddSubscriberDialog
